import { AppState, ComparisonEntityTypeViewState } from './types.js';
import { COMPARISON_DEFAULT_ENTITY_TYPES } from '../comparison/types.js';

export const state: AppState = {
  activeTab: 'overview',
  isEmbedded: false,
  health: {
    status: 'unknown',
    uptime: 0,
    version: '0.2.0',
    latency: null,
  },
  auth: {
    isAuthenticated: false,
    sessionToken: null,
    user: null,
    pimUrl: null,
    tenantId: null,
    authMode: 'akeneo_jwt',
    authBypass: false,
    isEmbeddedRequired: false,
    handshakeStatus: 'idle',
    errorMessage: null,
  },
  connections: {
    source: null,
    target: null,
    total: 0,
    list: [],
    filter: 'all',
    search: '',
    editingId: null,
    pendingDeleteId: null,
    testResults: {},
    isTestingForm: false,
  },
  jobs: {
    list: [],
    total: 0,
    stats: {
      total: 0,
      pending: 0,
      running: 0,
      completed: 0,
      failed: 0,
      cancelled: 0,
      total_entities: 0,
      processed_entities: 0,
      created_count: 0,
      updated_count: 0,
      failed_count: 0,
      skipped_count: 0,
    },
    filterStatus: '',
    filterType: '',
    search: '',
    page: 1,
    limit: 25,
    selectedJobId: null,
    selectedJob: null,
    isLivePolling: true,
    liveTimer: null,
  },
  logs: {
    list: [],
    total: 0,
    stats: {
      total: 0,
      info: 0,
      warn: 0,
      error: 0,
      debug: 0,
    },
    filterLevel: '',
    filterCategory: '',
    search: '',
    autoScroll: true,
    activeSubTab: 'jobs',
    page: 1,
    limit: 100,
  },
  monitoring: {
    realtime: {
      isRunning: true,
      isPaused: false,
      inFlightCount: 0,
      stats: {
        totalProcessed: 0,
        totalSuccess: 0,
        totalFailed: 0,
        totalRetried: 0,
        lastEventAt: null,
      },
    },
    polling: {
      isRunning: true,
      isPaused: false,
      isPolling: false,
      stats: {
        pollCount: 0,
        totalEntitiesPolled: 0,
        totalEntitiesSynced: 0,
        totalCreated: 0,
        totalUpdated: 0,
        totalUnchanged: 0,
        totalSkipped: 0,
        totalFailed: 0,
        lastPollStartedAt: null,
        lastPollCompletedAt: null,
        lastPollDurationMs: null,
        nextPollScheduledAt: null,
      },
      checkpoints: {},
      lastResult: null,
    },
    webhooks: {
      stats: {
        totalRequests: 0,
        successfulRequests: 0,
        failedRequests: 0,
        totalEvents: 0,
        lastRequestAt: null,
        invalidSignatureCount: 0,
        malformedCount: 0,
      },
      events: [],
      filterType: '',
      filterAction: '',
      search: '',
      selectedEvent: null,
    },
    queue: {
      stats: {
        queueSize: 0,
        isPaused: false,
        inFlightBatches: 0,
        totalBatchesDispatched: 0,
        totalEventsEnqueued: 0,
        totalEventsProcessed: 0,
        totalEventsFailed: 0,
        deadLetterQueueSize: 0,
        lastDispatchedAt: null,
      },
      pendingEvents: [],
      deadLetterItems: [],
    },
    activeSubTab: 'events',
    isLivePolling: true,
    liveTimer: null,
  },
  explorer: {
    entityType: 'products',
    search: '',
    enabledFilter: '',
    page: 1,
    limit: 25,
    totalCount: 0,
    hasMore: false,
    items: [],
    loading: false,
    selectedItemId: null,
    selectedItemIds: new Set<string>(),
    diffModal: null,
    searchAfter: undefined,
    nextCursor: undefined,
    cursorHistory: [undefined],
  },
  staging: {
    packages: [],
    selectedPackageId: null,
    selectedPackage: null,
    items: [],
    resolvedStages: [],
    loading: false,
    activeSubTab: 'packages',
    scenarioFilter: 'all',
    filterType: 'all',
    search: '',
    page: 1,
    limit: 10,
    selectedItemIds: new Set<string>(),
    inspectItem: null,
    inspectState: {
      item: null,
      activeTab: 'values',
      payloadDraft: '',
      isValidJson: true,
      validationError: null,
      isSaving: false,
      dependencies: null,
      loadingDependencies: false,
    },
    scenarioWizard: {
      selectedScenario: 'company_setup',
      packageName: 'Company Setup',
      packageDescription: 'Structural entity foundation: locales, channels, attributes, families, categories, and reference entities',
      companySetup: {
        includeLocales: true,
        includeCurrencies: true,
        includeChannels: true,
        includeAttributes: true,
        includeFamilies: true,
        includeCategories: true,
        includeReferenceEntities: true,
        includeMeasurementFamilies: true,
        autoFetchSource: false,
      },
      productBatch: {
        family: '',
        category: '',
        limit: 50,
        resolveDependencies: true,
        includeMedia: true,
        autoFetchSource: false,
      },
      backupSnapshot: {
        scope: 'full',
        includeMedia: true,
        autoFreeze: false,
        autoFetchSource: false,
      },
      availableFamilies: [],
      availableCategories: [],
      loadingOptions: false,
      isCreating: false,
      error: null,
    },
    curate: {
      newPackageName: '',
      newPackageDescription: '',
      entityType: 'products',
      entityId: '',
    },
    deploy: {
      targetConnectionId: '',
      dryRun: false,
      activeJobId: null,
      isRunning: false,
      isCompleted: false,
      status: 'idle',
      activeView: 'config',
      readiness: null,
      readinessLoading: false,
      dryRunResult: null,
      dryRunLoading: false,
      progressPollTimer: null,
      elapsedTimer: null,
      startTime: null,
      elapsedSeconds: 0,
      logs: [],
    },
    resolve: {
      activeJobId: null,
      isRunning: false,
      isCompleted: false,
      status: 'idle',
      progress: 0,
      progressPollTimer: null,
      lastResult: null,
    },
    import: {
      file: null,
      fileName: '',
      fileSize: 0,
      targetPackageId: '',
      mode: 'merge',
      scenario: 'custom',
      loading: false,
      uploadProgress: 0,
      statusText: '',
      result: null,
      error: null,
    },
  },
  backup: {
    scope: 'full',
    format: 'archive',
    restore: {
      file: null,
      fileName: '',
      dryRun: true,
      packageDir: null,
      manifest: null,
      loading: false,
      error: null,
    },
    importResult: null,
  },
  batch: {
    activeSubTab: 'catalog',
    summary: {},
    catalog: {
      entityType: 'products',
      categoryTree: [],
      selectedCategory: null,
      expandedNodes: new Set<string>(),
      categorySearch: '',
      families: [],
      channels: [],
      selectedFamilies: [],
      selectedChannels: [],
      filterStatus: '',
      filterCompleteness: '',
      filterUpdatedSince: '',
      search: '',
      items: [],
      selectedItemIds: new Set<string>(),
      page: 1,
      limit: 25,
      totalCount: 0,
      hasMore: false,
      loading: false,
    },
    orchestrator: {
      selectedEntityTypes: [
        'system_config',
        'locales',
        'currencies',
        'association_types',
        'categories',
        'attribute_groups',
        'attributes',
        'attribute_options',
        'families',
        'family_variants',
        'product_models',
        'products',
        'asset_families',
        'assets',
      ],
      mode: 'dryrun',
      strictMode: false,
      validateDependencies: true,
      checkReadiness: true,
      includeDiffs: true,
      forceSync: false,
      stopOnError: false,
      limit: null,
      topologicalOrder: [
        'system_config',
        'locales',
        'currencies',
        'association_types',
        'categories',
        'attribute_groups',
        'attributes',
        'attribute_options',
        'families',
        'family_variants',
        'product_models',
        'products',
        'asset_families',
        'assets',
      ],
      pruneEnabled: false,
      pruneThreshold: 15,
      forceDelete: false,
    },
    validation: {
      scanResult: null,
      diffPlan: null,
      filterEntity: 'all',
      filterAction: 'all',
      searchDiffs: '',
      expandedDiffIds: new Set<string>(),
    },
    inspectEntity: null,
    activeJobId: null,
    activeJobProgress: null,
    progressPollTimer: null,
  },
  comparison: {
    reports: [],
    activeReportId: null,
    loading: false,
    runningReportId: null,
    leftSide: 'source',
    rightSide: 'target',
    entityTypes: [...COMPARISON_DEFAULT_ENTITY_TYPES],
    expandedTypes: new Set<string>(),
    selectedEntity: null,
    diffLoading: false,
    diffModal: null,
    reportEntities: null,
    entityTypeStates: new Map<string, ComparisonEntityTypeViewState>(),
    reportFilter: 'all',
  },
  pollTimer: null,
};
